import { useEffect, useRef, useState } from "react";
import { Color, Mesh, Program, Renderer, Triangle } from "ogl";
import "./Aurora.css";

const vertexShader = `#version 300 es
in vec2 position;

void main() {
  gl_Position = vec4(position, 0.0, 1.0);
}
`;

const fragmentShader = `#version 300 es
precision highp float;

uniform float uTime;
uniform float uAmplitude;
uniform vec3 uColorStops[4];
uniform vec2 uResolution;
uniform float uBlend;

out vec4 fragColor;

vec3 permute(vec3 value) {
  return mod(((value * 34.0) + 1.0) * value, 289.0);
}

float simplexNoise(vec2 value) {
  const vec4 constants = vec4(
    0.211324865405187,
    0.366025403784439,
    -0.577350269189626,
    0.024390243902439
  );
  vec2 cell = floor(value + dot(value, constants.yy));
  vec2 offset = value - cell + dot(cell, constants.xx);
  vec2 corner = offset.x > offset.y
    ? vec2(1.0, 0.0)
    : vec2(0.0, 1.0);
  vec4 offsets = offset.xyxy + constants.xxzz;
  offsets.xy -= corner;
  cell = mod(cell, 289.0);

  vec3 permutation = permute(
    permute(cell.y + vec3(0.0, corner.y, 1.0)) +
    cell.x + vec3(0.0, corner.x, 1.0)
  );
  vec3 weights = max(
    0.5 - vec3(
      dot(offset, offset),
      dot(offsets.xy, offsets.xy),
      dot(offsets.zw, offsets.zw)
    ),
    0.0
  );
  weights = weights * weights;
  weights = weights * weights;

  vec3 gradient = 2.0 * fract(permutation * constants.www) - 1.0;
  vec3 height = abs(gradient) - 0.5;
  vec3 rounded = floor(gradient + 0.5);
  vec3 adjusted = gradient - rounded;
  weights *= 1.79284291400159 -
    0.85373472095314 * (adjusted * adjusted + height * height);

  vec3 contribution;
  contribution.x = adjusted.x * offset.x + height.x * offset.y;
  contribution.yz =
    adjusted.yz * offsets.xz + height.yz * offsets.yw;
  return 130.0 * dot(weights, contribution);
}

struct ColorStop {
  vec3 color;
  float position;
};

#define COLOR_RAMP(colors, factor, finalColor) { \
  int index = 0; \
  for (int i = 0; i < 3; i++) { \
    ColorStop currentColor = colors[i]; \
    bool isInBetween = currentColor.position <= factor; \
    index = int(mix(float(index), float(i), float(isInBetween))); \
  } \
  ColorStop currentColor = colors[index]; \
  ColorStop nextColor = colors[index + 1]; \
  float range = nextColor.position - currentColor.position; \
  float lerpFactor = (factor - currentColor.position) / range; \
  finalColor = mix(currentColor.color, nextColor.color, lerpFactor); \
}

void main() {
  vec2 uv = gl_FragCoord.xy / uResolution;

  ColorStop colors[4];
  colors[0] = ColorStop(uColorStops[0], 0.0);
  colors[1] = ColorStop(uColorStops[1], 0.333333);
  colors[2] = ColorStop(uColorStops[2], 0.666667);
  colors[3] = ColorStop(uColorStops[3], 1.0);

  vec3 rampColor;
  COLOR_RAMP(colors, uv.x, rampColor);

  float height = simplexNoise(
    vec2(uv.x * 2.0 + uTime * 0.1, uTime * 0.25)
  ) * 0.5 * uAmplitude;
  height = exp(height);
  height = uv.y * 2.0 - height + 0.2;
  float intensity = 0.6 * height;

  float midpoint = 0.20;
  float auroraAlpha = smoothstep(
    midpoint - uBlend * 0.5,
    midpoint + uBlend * 0.5,
    intensity
  );
  vec3 auroraColor = intensity * rampColor;

  fragColor = vec4(auroraColor * auroraAlpha, auroraAlpha);
}
`;

function useReducedMotion() {
  const [reducedMotion, setReducedMotion] = useState(
    () => window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handleChange = (event) => setReducedMotion(event.matches);
    mediaQuery.addEventListener?.("change", handleChange);
    return () => mediaQuery.removeEventListener?.("change", handleChange);
  }, []);

  return reducedMotion;
}

const toColorStops = (stops) =>
  stops.map((hex) => {
    const color = new Color(hex);
    return [color.r, color.g, color.b];
  });

export default function Aurora({
  colorStops = ["#f6efc1", "#e8c35d", "#aa803b", "#5e421f"],
  amplitude = 1,
  blend = 0.5,
  speed = 1,
  maxDpr = 1.5,
  className = "",
}) {
  const containerRef = useRef(null);
  const propsRef = useRef({ colorStops, amplitude, blend, speed });
  const reducedMotion = useReducedMotion();

  propsRef.current = { colorStops, amplitude, blend, speed };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const renderer = new Renderer({
      alpha: true,
      premultipliedAlpha: true,
      antialias: false,
      dpr: Math.min(window.devicePixelRatio || 1, maxDpr),
    });
    const { gl } = renderer;
    gl.clearColor(0, 0, 0, 0);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.canvas.style.backgroundColor = "transparent";

    const geometry = new Triangle(gl);
    if (geometry.attributes.uv) delete geometry.attributes.uv;

    const program = new Program(gl, {
      vertex: vertexShader,
      fragment: fragmentShader,
      uniforms: {
        uTime: { value: 0 },
        uAmplitude: { value: amplitude },
        uColorStops: { value: toColorStops(colorStops) },
        uResolution: {
          value: [container.offsetWidth, container.offsetHeight],
        },
        uBlend: { value: blend },
      },
    });
    const mesh = new Mesh(gl, { geometry, program });
    let animationFrame = 0;
    let destroyed = false;

    container.replaceChildren(gl.canvas);

    let renderedWidth = 0;
    let renderedHeight = 0;
    const resize = () => {
      const width = Math.max(1, Math.round(container.offsetWidth));
      const height = Math.max(1, Math.round(container.offsetHeight));
      if (width === renderedWidth && height === renderedHeight) return;
      renderedWidth = width;
      renderedHeight = height;
      renderer.setSize(width, height);
      program.uniforms.uResolution.value = [width, height];
    };
    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(container);
    resize();

    const render = (time = 0) => {
      if (destroyed || document.visibilityState === "hidden") return;

      const current = propsRef.current;
      program.uniforms.uTime.value = time * 0.001 * current.speed;
      program.uniforms.uAmplitude.value = current.amplitude;
      program.uniforms.uBlend.value = current.blend;
      program.uniforms.uColorStops.value = toColorStops(
        current.colorStops,
      );
      renderer.render({ scene: mesh });

      if (!reducedMotion) animationFrame = requestAnimationFrame(render);
    };

    const handleVisibilityChange = () => {
      cancelAnimationFrame(animationFrame);
      animationFrame = 0;
      if (document.visibilityState !== "hidden" && !destroyed) {
        animationFrame = requestAnimationFrame(render);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    if (reducedMotion) {
      render(0);
    } else {
      animationFrame = requestAnimationFrame(render);
    }

    return () => {
      destroyed = true;
      cancelAnimationFrame(animationFrame);
      resizeObserver.disconnect();
      document.removeEventListener(
        "visibilitychange",
        handleVisibilityChange,
      );
      gl.canvas.remove();
      gl.getExtension("WEBGL_lose_context")?.loseContext();
    };
  }, [amplitude, blend, colorStops, maxDpr, reducedMotion, speed]);

  return (
    <div
      ref={containerRef}
      className={`aurora-container ${className}`.trim()}
    />
  );
}
